import {
  collection,
  doc,
  getDocs,
  query,
  where,
  addDoc,
  updateDoc,
  deleteDoc,
  Timestamp,
  getDoc,
  orderBy,
  DocumentData,
  QueryDocumentSnapshot,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  MaintenancePlan,
  MaintenanceTask,
  MaintenanceAlert,
  MaintenanceAlertStatus
} from '../types/maintenance';
import { perplexityService } from './perplexityService';
import { calculateNextDue, calculatePriority } from '../utils/maintenanceUtils';

const PLANS_COLLECTION = 'maintenancePlans';
const TASKS_COLLECTION = 'maintenanceTasks';
const ALERTS_COLLECTION = 'maintenanceAlerts';

class MaintenancePlanService {
  async getVehiclePlans(vehicleId: string): Promise<MaintenancePlan[]> {
    try {
      const plansQuery = query(
        collection(db, PLANS_COLLECTION),
        where('vehicleId', '==', vehicleId),
        orderBy('createdAt', 'desc')
      );

      const plansDocs = await getDocs(plansQuery);
      
      // Récupérer les tâches pour chaque plan
      const plansWithTasks = await Promise.all(
        plansDocs.docs.map(async (planDoc) => {
          const planData = planDoc.data();
          const tasksQuery = collection(db, `${PLANS_COLLECTION}/${planDoc.id}/${TASKS_COLLECTION}`);
          const tasksDocs = await getDocs(tasksQuery);
          
          const tasks = tasksDocs.docs.map(taskDoc => ({
            id: taskDoc.id,
            ...taskDoc.data(),
            createdAt: taskDoc.data().createdAt,
            updatedAt: taskDoc.data().updatedAt
          }));

          return {
            id: planDoc.id,
            ...planData,
            tasks,
            createdAt: planData.createdAt,
            updatedAt: planData.updatedAt
          } as MaintenancePlan;
        })
      );

      return plansWithTasks;
    } catch (error) {
      console.error('Erreur lors de la récupération des plans:', error);
      throw error;
    }
  }

  async getPlan(planId: string): Promise<MaintenancePlan | null> {
    try {
      const planDoc = await getDoc(doc(db, PLANS_COLLECTION, planId));
      if (!planDoc.exists()) {
        return null;
      }

      const planData = planDoc.data();
      const tasksQuery = collection(db, `${PLANS_COLLECTION}/${planId}/${TASKS_COLLECTION}`);
      const tasksDocs = await getDocs(tasksQuery);
      
      const tasks = tasksDocs.docs.map(taskDoc => ({
        id: taskDoc.id,
        ...taskDoc.data(),
        createdAt: taskDoc.data().createdAt,
        updatedAt: taskDoc.data().updatedAt
      }));

      return {
        id: planDoc.id,
        ...planData,
        tasks,
        createdAt: planData.createdAt,
        updatedAt: planData.updatedAt
      } as MaintenancePlan;
    } catch (error) {
      console.error('Erreur lors de la récupération du plan:', error);
      throw error;
    }
  }

  async generatePlanFromPerplexity(
    vehicleId: string,
    organizationId: string,
    make: string,
    model: string,
    year: number,
    userId: string
  ): Promise<MaintenancePlan> {
    try {
      // Obtenir les recommandations de maintenance via Perplexity
      const recommendations = await perplexityService.getMaintenanceRecommendations(
        make,
        model,
        year
      );

      // Créer un nouveau plan sans les tâches
      const plan: Omit<MaintenancePlan, 'id' | 'tasks'> = {
        vehicleId,
        organizationId,
        name: `Plan ${make} ${model} ${year}`,
        description: 'Plan généré automatiquement via Perplexity',
        isAutoGenerated: true,
        createdBy: userId,
        updatedBy: userId,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      // Créer le plan dans Firestore
      const planRef = await addDoc(collection(db, PLANS_COLLECTION), plan);
      const planId = planRef.id;

      // Créer les tâches comme sous-collection
      const tasksPromises = recommendations.map(async (rec: any) => {
        const task: Omit<MaintenanceTask, 'id'> = {
          planId,
          name: rec.name,
          description: rec.description,
          frequency: rec.frequency,
          priority: rec.priority,
          notificationSettings: {
            reminderFrequency: 7 // Par défaut, rappel hebdomadaire
          },
          createdBy: userId,
          updatedBy: userId,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        };

        const taskRef = await addDoc(
          collection(db, `${PLANS_COLLECTION}/${planId}/${TASKS_COLLECTION}`),
          task
        );
        return { ...task, id: taskRef.id };
      });

      const createdTasks = await Promise.all(tasksPromises);

      return {
        ...plan,
        id: planId,
        tasks: createdTasks
      };
    } catch (error) {
      console.error('Erreur lors de la génération du plan:', error);
      throw error;
    }
  }

  async createCustomPlan(
    plan: Omit<MaintenancePlan, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<MaintenancePlan> {
    try {
      const { tasks, ...planWithoutTasks } = plan;
      const now = Timestamp.now();
      
      const planData = {
        ...planWithoutTasks,
        createdAt: now,
        updatedAt: now
      };

      // Créer le plan sans les tâches
      const planRef = await addDoc(collection(db, PLANS_COLLECTION), planData);
      const planId = planRef.id;

      // Créer les tâches comme sous-collection
      const tasksPromises = tasks.map(async (task) => {
        const taskData: Omit<MaintenanceTask, 'id'> = {
          ...task,
          planId,
          frequency: {
            ...task.frequency,
            lastCompletedMileage: task.frequency.lastCompletedMileage || 0,
            lastCompletedDate: task.frequency.lastCompletedDate || now,
            mileageValue: task.frequency.mileageValue || 0,
            timeValue: task.frequency.timeValue || 0
          },
          createdAt: now,
          updatedAt: now
        };

        const taskRef = await addDoc(
          collection(db, `${PLANS_COLLECTION}/${planId}/${TASKS_COLLECTION}`),
          taskData
        );
        return { ...taskData, id: taskRef.id };
      });

      const createdTasks = await Promise.all(tasksPromises);

      return {
        ...planData,
        id: planId,
        tasks: createdTasks
      };
    } catch (error) {
      console.error('Erreur lors de la création du plan:', error);
      throw error;
    }
  }

  async updatePlan(
    planId: string,
    updates: Partial<MaintenancePlan>
  ): Promise<void> {
    try {
      const { tasks, ...planUpdates } = updates;
      const updateData = {
        ...planUpdates,
        updatedAt: Timestamp.now()
      };

      // Mettre à jour le plan
      await updateDoc(doc(db, PLANS_COLLECTION, planId), updateData);

      // Si des tâches sont fournies, les mettre à jour
      if (tasks) {
        // Supprimer toutes les tâches existantes
        const existingTasksQuery = collection(db, `${PLANS_COLLECTION}/${planId}/${TASKS_COLLECTION}`);
        const existingTasksDocs = await getDocs(existingTasksQuery);
        await Promise.all(
          existingTasksDocs.docs.map(taskDoc => 
            deleteDoc(doc(db, `${PLANS_COLLECTION}/${planId}/${TASKS_COLLECTION}`, taskDoc.id))
          )
        );

        // Créer les nouvelles tâches
        await Promise.all(
          tasks.map(task => 
            addDoc(collection(db, `${PLANS_COLLECTION}/${planId}/${TASKS_COLLECTION}`), {
              ...task,
              planId,
              updatedAt: Timestamp.now()
            })
          )
        );
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour du plan:', error);
      throw error;
    }
  }

  async deletePlan(planId: string): Promise<void> {
    try {
      // Supprimer toutes les tâches de la sous-collection
      const tasksQuery = collection(db, `${PLANS_COLLECTION}/${planId}/${TASKS_COLLECTION}`);
      const tasksDocs = await getDocs(tasksQuery);
      await Promise.all(
        tasksDocs.docs.map(taskDoc => 
          deleteDoc(doc(db, `${PLANS_COLLECTION}/${planId}/${TASKS_COLLECTION}`, taskDoc.id))
        )
      );

      // Supprimer le plan
      await deleteDoc(doc(db, PLANS_COLLECTION, planId));
    } catch (error) {
      console.error('Erreur lors de la suppression du plan:', error);
      throw error;
    }
  }

  async createAlert(
    task: MaintenanceTask,
    vehicle: { id: string; organizationId: string; currentMileage: number; averageDailyMileage: number },
    userId: string
  ): Promise<MaintenanceAlert> {
    try {
      const now = Timestamp.now();
      const nextDue = calculateNextDue(task, vehicle.currentMileage, vehicle.averageDailyMileage);
      
      const alert: Omit<MaintenanceAlert, 'id'> = {
        taskId: task.id,
        vehicleId: vehicle.id,
        organizationId: vehicle.organizationId,
        status: 'active',
        priority: calculatePriority(nextDue.effectiveDueDate),
        dueDate: Timestamp.fromDate(nextDue.effectiveDueDate),
        mileageTarget: nextDue.nextMileageDue,
        timeTarget: nextDue.nextTimeDue ? Timestamp.fromDate(nextDue.nextTimeDue) : undefined,
        notificationsSent: 0,
        createdAt: now,
        updatedAt: now,
        createdBy: userId,
        updatedBy: userId
      };

      const alertRef = await addDoc(collection(db, ALERTS_COLLECTION), alert);
      return { ...alert, id: alertRef.id };
    } catch (error) {
      console.error('Erreur lors de la création de l\'alerte:', error);
      throw error;
    }
  }

  async updateAlertStatus(
    alertId: string,
    status: MaintenanceAlertStatus,
    maintenanceRecordId?: string
  ): Promise<void> {
    try {
      const updateData: Partial<MaintenanceAlert> = {
        status,
        updatedAt: Timestamp.now()
      };

      if (status === 'closed') {
        updateData.closedAt = Timestamp.now();
        if (maintenanceRecordId) {
          updateData.maintenanceRecordId = maintenanceRecordId;
        }
      }

      await updateDoc(doc(db, ALERTS_COLLECTION, alertId), updateData);
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut de l\'alerte:', error);
      throw error;
    }
  }

  async getActiveAlerts(vehicleId: string): Promise<MaintenanceAlert[]> {
    try {
      const alertsQuery = query(
        collection(db, ALERTS_COLLECTION),
        where('vehicleId', '==', vehicleId),
        where('status', '==', 'active')
      );

      const alertsDocs = await getDocs(alertsQuery);
      return alertsDocs.docs.map(doc => ({ id: doc.id, ...doc.data() } as MaintenanceAlert));
    } catch (error) {
      console.error('Erreur lors de la récupération des alertes:', error);
      throw error;
    }
  }

  async updateAlertNotification(
    alertId: string,
    notificationsSent: number
  ): Promise<void> {
    try {
      await updateDoc(doc(db, ALERTS_COLLECTION, alertId), {
        notificationsSent,
        lastNotificationDate: Timestamp.now()
      });
    } catch (error) {
      console.error('Erreur lors de la mise à jour des notifications:', error);
      throw error;
    }
  }
}

export const maintenancePlanService = new MaintenancePlanService();
